import type { BackendModule, ReadCallback } from "i18next";

import { canonicalLocale, type SupportedLocale } from "./locales";

export type LocaleBundle = Record<string, Record<string, unknown>>;

/**
 * 界面文案的 namespace 全集。
 *
 * 以前这份清单是 `Object.keys(resources["zh-CN"])` 现算出来的;语言包改成按需加载后
 * 入口里不再有同步 resources 可取,只能显式列。tests/spa-i18n-lazy-locales.test.ts 会拿
 * 它跟磁盘上的 JSON 文件对账,新增 namespace 时漏改就会红。
 */
export const LOCALE_NAMESPACES = [
  "auth",
  "billing",
  "catalogs",
  "common",
  "customers",
  "dashboard",
  "errors",
  "generation",
  "knowledge",
  "members",
  "mobile",
  "navigation",
  "notifications",
  "outputs",
  "products",
  "proposals",
  "public",
  "settings",
  "showcase",
  "styleRestoration",
  "xiaohongshu",
] as const;

const LOCALE_LOADERS: Record<SupportedLocale, () => Promise<{ default: LocaleBundle }>> = {
  "zh-CN": () => import("./locales/zh-CN/index"),
  "en-US": () => import("./locales/en-US/index"),
  "de-DE": () => import("./locales/de-DE/index"),
};

const bundleCache = new Map<SupportedLocale, Promise<LocaleBundle>>();

/**
 * 一门语言只拉一次。
 *
 * i18next 会为 19 个 namespace 各调一次 backend.read(它不用 readMulti),没有这层缓存
 * 就是 19 次 import()。缓存的是 Promise 而不是结果,所以并发的 19 次 read 共享同一次
 * 请求。失败时把 entry 摘掉,让 i18next 自带的退避重试能真正重新发请求,而不是永远
 * 拿到那个已经 reject 的 Promise。
 */
export function loadLocaleBundle(locale: SupportedLocale): Promise<LocaleBundle> {
  const cached = bundleCache.get(locale);
  if (cached) return cached;
  const pending = LOCALE_LOADERS[locale]().then(
    (module) => module.default,
    (error: unknown) => {
      bundleCache.delete(locale);
      throw error;
    },
  );
  bundleCache.set(locale, pending);
  return pending;
}

/**
 * 用 i18next 的 backend 插件而不是自建 changeUiLocale() 包装函数,是为了让现有 6 处
 * `i18n.changeLanguage(...)` 调用点一行都不用改就自动按需加载,也杜绝将来新增切换入口
 * 时有人忘了走包装函数。
 */
export const localeResourceBackend: BackendModule = {
  type: "backend",
  init(): void {},
  read(language: string, namespace: string, callback: ReadCallback): void {
    const locale = canonicalLocale(language);
    if (!locale) {
      callback(null, {});
      return;
    }
    loadLocaleBundle(locale).then(
      (bundle) => callback(null, bundle[namespace] ?? {}),
      (error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), null),
    );
  },
};
